import { useState, useEffect, useCallback, useMemo } from "react";
import {
  listTickers,
  getTickerStats,
  createTicker,
  updateTickerStatus,
  type TickerUniverse,
  type TickerUniverseStats,
  type TickerStatus,
  type TickerSource,
} from "../../services/adminService";

interface AddTickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (ticker: string, notes?: string) => Promise<void>;
}

function AddTickerModal({ isOpen, onClose, onAdd }: AddTickerModalProps) {
  const [ticker, setTicker] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticker.trim()) return;

    try {
      setLoading(true);
      setError(null);
      await onAdd(ticker.trim().toUpperCase(), notes.trim() || undefined);
      setTicker("");
      setNotes("");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add ticker");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-surface-800 rounded-xl p-6 w-full max-w-md border border-surface-700">
        <h3 className="text-lg font-semibold text-surface-100 mb-4">Add New Ticker</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Ticker Symbol
              </label>
              <input
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="e.g., AAPL"
                className="w-full px-3 py-2 bg-surface-900 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={10}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why is this ticker being added?"
                className="w-full px-3 py-2 bg-surface-900 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={2}
              />
            </div>
            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-700 text-surface-200 rounded-lg hover:bg-surface-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !ticker.trim()}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Adding..." : "Add Ticker"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

type SortField = "ticker" | "status" | "source" | "fail_count" | "last_warmed_at";
type SortDirection = "asc" | "desc";

export function TickerUniversePanel() {
  const [tickers, setTickers] = useState<TickerUniverse[]>([]);
  const [stats, setStats] = useState<TickerUniverseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);

  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<TickerStatus | "">("");
  const [sourceFilter, setSourceFilter] = useState<TickerSource | "">("");
  const [sortField, setSortField] = useState<SortField>("ticker");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [tickerData, statsData] = await Promise.all([
        listTickers(),
        getTickerStats(),
      ]);
      setTickers(tickerData.tickers || []);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load ticker data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) {
      loadData();
    }
  }, [expanded, loadData]);

  const handleAddTicker = async (ticker: string, notes?: string) => {
    await createTicker(ticker, notes);
    await loadData();
  };

  const handleToggleStatus = async (ticker: TickerUniverse) => {
    try {
      setUpdating(ticker.ticker);
      const newStatus: TickerStatus = ticker.status === "active" ? "inactive" : "active";
      await updateTickerStatus(ticker.ticker, newStatus);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update ticker");
    } finally {
      setUpdating(null);
    }
  };

  // Filter and sort tickers
  const filteredTickers = useMemo(() => {
    let result = [...tickers];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((t) => t.ticker.toLowerCase().includes(query));
    }

    // Apply status filter
    if (statusFilter) {
      result = result.filter((t) => t.status === statusFilter);
    }

    // Apply source filter
    if (sourceFilter) {
      result = result.filter((t) => t.source === sourceFilter);
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "ticker":
          comparison = a.ticker.localeCompare(b.ticker);
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        case "source":
          comparison = a.source.localeCompare(b.source);
          break;
        case "fail_count":
          comparison = a.fail_count - b.fail_count;
          break;
        case "last_warmed_at":
          const aDate = a.last_warmed_at ? new Date(a.last_warmed_at).getTime() : 0;
          const bDate = b.last_warmed_at ? new Date(b.last_warmed_at).getTime() : 0;
          comparison = aDate - bDate;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [tickers, searchQuery, statusFilter, sourceFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <svg
      className={`w-4 h-4 inline-block ml-1 ${sortField === field ? "text-blue-400" : "text-surface-500"}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {sortField === field && sortDirection === "desc" ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      )}
    </svg>
  );

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const sourceColors: Record<TickerSource, string> = {
    seed: "bg-blue-500/20 text-blue-400",
    discovered: "bg-green-500/20 text-green-400",
    manual: "bg-purple-500/20 text-purple-400",
  };

  return (
    <div className="rounded-xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
      {/* Header - clickable to expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-cyan-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-surface-100">Ticker Universe</div>
            <div className="text-xs text-surface-400">
              Manage symbols for cache warming and backtests
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-surface-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-surface-700/50 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-400">
              <p>{error}</p>
              <button
                onClick={loadData}
                className="mt-2 text-sm text-blue-400 hover:text-blue-300"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-surface-900/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-surface-100">{stats.total_tickers}</div>
                    <div className="text-xs text-surface-400">Total Tickers</div>
                  </div>
                  <div className="bg-surface-900/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-400">{stats.active_tickers}</div>
                    <div className="text-xs text-surface-400">Active</div>
                  </div>
                  <div className="bg-surface-900/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-surface-500">{stats.inactive_tickers}</div>
                    <div className="text-xs text-surface-400">Inactive</div>
                  </div>
                  <div className="bg-surface-900/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-amber-400">{stats.tickers_with_failures}</div>
                    <div className="text-xs text-surface-400">With Failures</div>
                  </div>
                </div>
              )}

              {/* Source breakdown */}
              {stats && (
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 text-xs">Seed</span>
                    <span className="text-surface-300">{stats.seed_tickers}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400 text-xs">Discovered</span>
                    <span className="text-surface-300">{stats.discovered_tickers}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-xs">Manual</span>
                    <span className="text-surface-300">{stats.manual_tickers}</span>
                  </div>
                </div>
              )}

              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tickers..."
                    className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as TickerStatus | "")}
                  className="px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

                {/* Source Filter */}
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value as TickerSource | "")}
                  className="px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Sources</option>
                  <option value="seed">Seed</option>
                  <option value="discovered">Discovered</option>
                  <option value="manual">Manual</option>
                </select>

                {/* Add Button */}
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Ticker
                </button>
              </div>

              {/* Results count */}
              <div className="text-xs text-surface-500">
                Showing {filteredTickers.length} of {tickers.length} tickers
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-700">
                      <th
                        className="px-3 py-2 text-left text-surface-400 font-medium cursor-pointer hover:text-surface-200"
                        onClick={() => handleSort("ticker")}
                      >
                        Ticker <SortIcon field="ticker" />
                      </th>
                      <th
                        className="px-3 py-2 text-left text-surface-400 font-medium cursor-pointer hover:text-surface-200"
                        onClick={() => handleSort("status")}
                      >
                        Status <SortIcon field="status" />
                      </th>
                      <th
                        className="px-3 py-2 text-left text-surface-400 font-medium cursor-pointer hover:text-surface-200"
                        onClick={() => handleSort("source")}
                      >
                        Source <SortIcon field="source" />
                      </th>
                      <th
                        className="px-3 py-2 text-left text-surface-400 font-medium cursor-pointer hover:text-surface-200"
                        onClick={() => handleSort("fail_count")}
                      >
                        Fails <SortIcon field="fail_count" />
                      </th>
                      <th
                        className="px-3 py-2 text-left text-surface-400 font-medium cursor-pointer hover:text-surface-200"
                        onClick={() => handleSort("last_warmed_at")}
                      >
                        Last Warmed <SortIcon field="last_warmed_at" />
                      </th>
                      <th className="px-3 py-2 text-right text-surface-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-700/50">
                    {filteredTickers.slice(0, 100).map((ticker) => (
                      <tr key={ticker.ticker} className="hover:bg-surface-800/50">
                        <td className="px-3 py-2">
                          <span className="font-mono font-medium text-surface-100">
                            {ticker.ticker}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              ticker.status === "active"
                                ? "bg-green-500/20 text-green-400"
                                : "bg-surface-600/50 text-surface-400"
                            }`}
                          >
                            {ticker.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${sourceColors[ticker.source]}`}>
                            {ticker.source}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {ticker.fail_count > 0 ? (
                            <span className="text-amber-400">{ticker.fail_count}</span>
                          ) : (
                            <span className="text-surface-500">0</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-surface-400 text-xs">
                          {formatDate(ticker.last_warmed_at)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleToggleStatus(ticker)}
                            disabled={updating === ticker.ticker}
                            className={`px-3 py-1 rounded text-xs transition-colors ${
                              ticker.status === "active"
                                ? "bg-surface-700 text-surface-300 hover:bg-red-500/20 hover:text-red-400"
                                : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            } disabled:opacity-50`}
                          >
                            {updating === ticker.ticker ? (
                              <span className="animate-pulse">...</span>
                            ) : ticker.status === "active" ? (
                              "Deactivate"
                            ) : (
                              "Activate"
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredTickers.length > 100 && (
                  <div className="text-center py-2 text-xs text-surface-500">
                    Showing first 100 results. Use search to filter.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Ticker Modal */}
      <AddTickerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddTicker}
      />
    </div>
  );
}
